import { useEffect, useMemo, useState } from 'react';
import {
  ALL_ACTIVE_PLACES,
  getSeoApproximateLocation,
  isActiveCountry,
  LocationFullType,
} from '../../../components/Location/config';
import {
  getLocationFromActivePlace,
  getNearestActiveLocation,
} from '../../../components/Location/helpers';
import { locationActionCreators } from '../../../redux/slices/location/actionCreators';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import { usePointLocation } from '../../../hooks/hook.location';
import { useIonRouter } from '@ionic/react';

// Getting catalog link, including geo information
export const useLocationRedirector = ({ allowSet }: { allowSet: boolean }) => {
  const router = useIonRouter();
  const pointLocation = usePointLocation();

  const dispatch = useTypedDispatch();
  const lockedLocation = useTypedSelector(s => s.location.lockedLocation);
  const locationAlreadySelected = !!useTypedSelector(
    s => s.location.selectedLocation
  );

  const [processed, setProcessed] = useState(false);

  const [fakeLockedLocation, setFakeLockedLocation] = useState<
    LocationFullType & { seoLine: string }
  >();

  useEffect(() => {
    (async () => {
      if (!pointLocation) return undefined;
      if (!allowSet) {
        setProcessed(true);
        return undefined;
      }

      const { nearestActiveLocation, nearestActiveLocationSeoLine } =
        getNearestActiveLocation(pointLocation);

      if (!isActiveCountry(pointLocation.country)) {
        await dispatch(
          locationActionCreators.lockLocation({
            nearestActiveLocation,
            nearestActiveLocationSeoLine,
          })
        );
      } else if (!locationAlreadySelected) {
        // To avoid highligting on Catalog page
        setFakeLockedLocation({
          ...nearestActiveLocation,
          seoLine: nearestActiveLocationSeoLine,
        });
        dispatch(locationActionCreators.selectLocation(nearestActiveLocation));
      }

      setProcessed(true);
    })();
  }, [dispatch, pointLocation, locationAlreadySelected, allowSet]);

  const predefinedLocation = useMemo(() => {
    const activePlace = ALL_ACTIVE_PLACES.find(
      p => p.placeId === pointLocation?.place_id
    );
    if (activePlace) {
      return {
        ...getLocationFromActivePlace(activePlace),
        seoLine: activePlace.seoLine,
      };
    }
  }, [pointLocation]);

  if (!processed || !pointLocation) return null;

  const seoLine =
    lockedLocation?.seoLine ||
    fakeLockedLocation?.seoLine ||
    predefinedLocation?.seoLine ||
    getSeoApproximateLocation(pointLocation);

  const isPlaceIdRequired = !(
    lockedLocation ||
    fakeLockedLocation ||
    predefinedLocation
  );
  const placeId = isPlaceIdRequired ? pointLocation.place_id : null;

  const search = router.routeInfo.search || '';
  const symbol = !!search ? '&' : '?';
  const searchWithPlaceId =
    search.includes('place_id') || !placeId
      ? search // no need because place_id already exists
      : search + `${symbol}place_id=${placeId}`;

  return `/catalog/${seoLine + searchWithPlaceId}`;
};
